import { useCallback, useEffect, useState } from "react";
import { subscriptionsService } from "../features/subscriptions/service";
import type {
  SubscriptionBlock,
  PlanInfo,
  BillingEvent,
  CancellationReason,
  TierLimitsResponse,
  TierPricing,
  FeatureCatalogEntry,
  PaymentOptions,
  MyUsageResponse,
} from "../features/subscriptions/types";
import { buildFeatureRows } from "../features/subscriptions/planFeatures";
import { parseApiError } from "../lib/http/apiErrors";
import { formatISTMonthDayYear } from "../utils/dateUtils";
import FeatureInfoTooltip from "../components/common/FeatureInfoTooltip";

const TIER_LABELS: Record<string, string> = {
  free: "Free",
  starter: "Starter",
  mid: "Mid",
  premium: "Premium",
  pro: "Pro",
  studio: "Studio",
  elite: "Elite",
};

const TIER_COLORS: Record<string, string> = {
  free: "bg-gray-700 text-gray-300",
  starter: "bg-blue-900 text-blue-300",
  mid: "bg-indigo-900 text-indigo-300",
  premium: "bg-purple-900 text-purple-300",
  pro: "bg-teal-900 text-teal-300",
  studio: "bg-cyan-900 text-cyan-300",
  elite: "bg-amber-900 text-amber-300",
};

const STATUS_COLORS: Record<string, string> = {
  trialing: "text-blue-400",
  active: "text-green-400",
  past_due: "text-yellow-400",
  cancelled: "text-orange-400",
  expired: "text-red-400",
};

const CANCELLATION_REASONS: { value: CancellationReason; label: string }[] = [
  { value: "too_expensive", label: "Too expensive" },
  { value: "not_using", label: "Not using it enough" },
  { value: "missing_feature", label: "Missing a feature" },
  { value: "switching", label: "Switching to another service" },
  { value: "other", label: "Other" },
];

function TierBadge({ tier }: { tier: string }) {
  return (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${TIER_COLORS[tier] ?? "bg-gray-700 text-gray-300"}`}
    >
      {TIER_LABELS[tier] ?? tier}
    </span>
  );
}

function formatDate(iso: string | null) {
  if (!iso) return "—";
  return formatISTMonthDayYear(iso);
}

function formatAmount(paise: number | null, currency = "INR") {
  if (paise === null) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(paise / 100);
}

/** Price header for a plan card. `pricing.*` prices are whole currency units
 * (rupees), so ×100 into formatAmount which expects the minor unit. */
function PriceBlock({ pricing, currency }: { pricing?: TierPricing; currency: string }) {
  if (!pricing) return null;
  if (pricing.final_price <= 0) {
    return <div className="mb-4"><span className="text-2xl font-bold text-white">Free</span></div>;
  }
  const discounted = !!pricing.discount && pricing.final_price < pricing.list_price;
  const discountLabel = !pricing.discount
    ? ""
    : pricing.discount.type === "percent"
      ? `${pricing.discount.value}% off`
      : `${formatAmount(pricing.discount.value * 100, currency)} off`;
  return (
    <div className="mb-4">
      <div className="flex items-baseline gap-2 flex-wrap">
        {discounted && (
          <span className="text-base text-gray-500 line-through">
            {formatAmount(pricing.list_price * 100, currency)}
          </span>
        )}
        <span className="text-2xl font-bold text-white">
          {formatAmount(pricing.final_price * 100, currency)}
        </span>
        <span className="text-gray-500 text-xs">/month</span>
        {discounted && (
          <span className="px-1.5 py-0.5 rounded-md bg-emerald-900/50 text-emerald-300 text-[10px] font-semibold">
            {discountLabel}
          </span>
        )}
      </div>
      <span className="text-gray-500 text-xs">
        ≈ {pricing.credits_equivalent.toLocaleString()} credits/mo
      </span>
    </div>
  );
}

export default function SubscriptionPage() {
  const [subscription, setSubscription] = useState<SubscriptionBlock | null>(
    null
  );
  const [plans, setPlans] = useState<PlanInfo[]>([]);
  const [userRole, setUserRole] = useState<"seller" | "buyer">("seller");
  const [tierLimits, setTierLimits] = useState<TierLimitsResponse | null>(null);
  const [featureCatalog, setFeatureCatalog] = useState<Record<string, FeatureCatalogEntry>>({});
  const [events, setEvents] = useState<BillingEvent[]>([]);
  const [tab, setTab] = useState<"overview" | "plans" | "billing">("overview");
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [paymentOptions, setPaymentOptions] = useState<PaymentOptions | null>(null);
  const [selectedGateway, setSelectedGateway] = useState<string>("");
  const [usage, setUsage] = useState<MyUsageResponse | null>(null);

  // Cancel modal state
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState<CancellationReason>(
    "too_expensive"
  );
  const [cancelNote, setCancelNote] = useState("");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [sub, plansRes, billingRes] = await Promise.all([
        subscriptionsService.getMySubscription(),
        subscriptionsService.getPlans(),
        subscriptionsService.getBillingHistory(50),
      ]);
      setSubscription(sub);
      const role: "seller" | "buyer" = plansRes.role === "buyer" ? "buyer" : "seller";
      setUserRole(role);
      // Only show plans that match the user's own role
      setPlans(plansRes.plans.filter((p) => p.role === role));
      setEvents(billingRes.events);
      subscriptionsService
        .getTierLimits(role)
        .then(setTierLimits)
        .catch(() => setTierLimits(null));
      subscriptionsService
        .getMyUsage()
        .then(setUsage)
        .catch(() => setUsage(null));
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    subscriptionsService
      .getFeatureCatalog()
      .then((data) => {
        const byAction: Record<string, FeatureCatalogEntry> = {};
        for (const f of data.features) byAction[f.action] = f;
        setFeatureCatalog(byAction);
      })
      .catch(() => setFeatureCatalog({}));
  }, []);

  useEffect(() => {
    subscriptionsService
      .getPaymentOptions()
      .then((opts) => {
        setPaymentOptions(opts);
        setSelectedGateway(opts.default || opts.enabled[0] || "");
      })
      .catch(() => setPaymentOptions(null));
  }, []);

  const handleSubscribe = async (plan_key: string) => {
    setActionLoading(true);
    setError(null);
    try {
      const res = await subscriptionsService.createSubscription(
        plan_key,
        paymentOptions?.show_picker ? selectedGateway || undefined : undefined
      );
      if (res.provider === "cashfree" && res.subscription_session_id) {
        // Cashfree subscription authorization via the v3 JS SDK. The local
        // tier is activated server-side by the SUBSCRIPTION_STATUS_CHANGE
        // webhook once the mandate is authorized.
        const w = window as unknown as {
          Cashfree?: (opts: { mode: string }) => {
            subscriptionsCheckout: (opts: {
              subsSessionId: string;
              redirectTarget?: string;
            }) => Promise<unknown>;
          };
        };
        if (!w.Cashfree) {
          await new Promise<void>((resolve, reject) => {
            const s = document.createElement("script");
            s.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
            s.onload = () => resolve();
            s.onerror = () => reject(new Error("Failed to load Cashfree"));
            document.body.appendChild(s);
          });
        }
        if (!w.Cashfree) {
          throw new Error("Cashfree checkout unavailable");
        }
        const cashfree = w.Cashfree({
          mode: res.mode === "production" ? "production" : "sandbox",
        });
        await cashfree.subscriptionsCheckout({
          subsSessionId: res.subscription_session_id,
          // Open the mandate in the SAME tab. Cashfree redirects back to the
          // backend-configured return_url (/subscription/success) after the
          // mandate completes, where activation is polled.
          redirectTarget: "_self",
        });
        return;
      }
      if (res.checkout_url) {
        window.location.href = res.checkout_url;
        return;
      }
      setSuccess("Subscription started!");
      await fetchAll();
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpgrade = async (plan_key: string) => {
    if (!confirm("Upgrade now? Your tier will change immediately.")) return;
    setActionLoading(true);
    setError(null);
    try {
      await subscriptionsService.upgrade(plan_key);
      setSuccess("Upgraded successfully.");
      await fetchAll();
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDowngrade = async (plan_key: string) => {
    if (
      !confirm(
        "Downgrade at end of billing cycle? Your current tier is kept until then."
      )
    )
      return;
    setActionLoading(true);
    setError(null);
    try {
      await subscriptionsService.downgrade(plan_key);
      setSuccess("Downgrade scheduled for end of billing cycle.");
      await fetchAll();
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async () => {
    setActionLoading(true);
    setError(null);
    try {
      await subscriptionsService.reactivate();
      setSuccess("Subscription reactivated.");
      await fetchAll();
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    setActionLoading(true);
    setError(null);
    try {
      await subscriptionsService.cancel(
        cancelReason,
        cancelNote || undefined
      );
      setSuccess(
        "Cancelled. You keep access until the end of your billing period."
      );
      setShowCancelModal(false);
      await fetchAll();
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setActionLoading(false);
    }
  };

  // `tier` from GET /subscriptions/me is already the effective tier (expiry +
  // admin grant overlay/lapse applied server-side).
  const currentTier = subscription?.tier ?? "free";

  const isActive =
    subscription?.status === "active" ||
    subscription?.status === "trialing" ||
    subscription?.status === "past_due" ||
    subscription?.status === "cancelled";

  const isCancelled = subscription?.status === "cancelled";
  const isExpiredOrFree =
    !subscription || subscription.status === "expired" || subscription.tier === "free";

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-gray-400">Loading subscription...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-4xl mx-auto px-4 py-10">
        <h1 className="text-3xl font-bold mb-2">Subscription</h1>
        <p className="text-gray-400 mb-8">Manage your plan and billing</p>

        {error && (
          <div className="mb-4 px-4 py-3 bg-red-900/40 border border-red-700 rounded-xl text-red-300 text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 px-4 py-3 bg-green-900/40 border border-green-700 rounded-xl text-green-300 text-sm">
            {success}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-8 bg-gray-900 rounded-xl p-1 w-fit">
          {(["overview", "plans", "billing"] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${
                tab === t
                  ? "bg-indigo-600 text-white"
                  : "text-gray-400 hover:text-white"
              }`}
            >
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {tab === "overview" && subscription && (
          <div className="space-y-6">
            <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <p className="text-sm text-gray-400 mb-2">Current Plan</p>
                  <TierBadge tier={currentTier} />
                  {subscription.admin_granted && (
                    <span className="ml-2 text-xs text-amber-400">
                      (Admin granted until {formatDate(subscription.admin_granted_until)})
                    </span>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-400 mb-1">Status</p>
                  <span
                    className={`font-semibold capitalize ${STATUS_COLORS[subscription.status] ?? "text-gray-300"}`}
                  >
                    {subscription.status.replace("_", " ")}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                {subscription.current_period_end && (
                  <div>
                    <p className="text-gray-500">Period ends</p>
                    <p className="text-white">{formatDate(subscription.current_period_end)}</p>
                  </div>
                )}
                {subscription.last_payment_at && (
                  <div>
                    <p className="text-gray-500">Last payment</p>
                    <p className="text-white">
                      {formatAmount(subscription.last_invoice_amount_paise, subscription.last_invoice_currency)} on{" "}
                      {formatDate(subscription.last_payment_at)}
                    </p>
                  </div>
                )}
                {subscription.next_charge_at && (
                  <div>
                    <p className="text-gray-500">Next charge</p>
                    <p className="text-white">{formatDate(subscription.next_charge_at)}</p>
                  </div>
                )}
                {subscription.cancel_at_period_end && (
                  <div className="col-span-2">
                    <p className="text-orange-400 text-sm">
                      Cancels at end of period ({formatDate(subscription.current_period_end)})
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Usage this month — tier-aware, from GET /api/v1/subscriptions/me/usage */}
            {usage && (
              <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800">
                <p className="text-sm text-gray-400 mb-4">Usage this month</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {Object.values(usage.usage)
                    .filter((info) => info.monthly.limit !== 0 || info.monthly.used > 0)
                    .map((info) => {
                      const label = featureCatalog[info.action]?.label ?? info.action;
                      const { used, limit, remaining } = info.monthly;
                      const uncapped = limit === null;
                      const pct = uncapped || !limit ? 0 : Math.min(100, (used / limit) * 100);
                      const low = !uncapped && remaining !== null && remaining <= Math.max(1, Math.ceil((limit ?? 1) * 0.1));
                      return (
                        <div key={info.action} className="space-y-1.5">
                          <div className="flex items-baseline justify-between text-sm">
                            <span className="text-gray-300 capitalize">{label}</span>
                            <span className={low ? "text-amber-400" : "text-gray-400"}>
                              {used}
                              {uncapped ? "" : ` / ${limit}`}
                            </span>
                          </div>
                          {!uncapped && (
                            <div className="h-1.5 rounded-full bg-gray-800 overflow-hidden">
                              <div
                                className={`h-full rounded-full ${low ? "bg-amber-500" : "bg-indigo-500"}`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          )}
                          {info.monthly.warning && (
                            <p className="text-xs text-amber-400">{info.monthly.warning}</p>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex flex-wrap gap-3">
              {isExpiredOrFree && (
                <button
                  onClick={() => setTab("plans")}
                  className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 rounded-xl text-sm font-medium transition-colors"
                >
                  Upgrade Plan
                </button>
              )}
              {isCancelled && !subscription.cancel_at_period_end && (
                <button
                  onClick={handleReactivate}
                  disabled={actionLoading}
                  className="px-5 py-2.5 bg-green-700 hover:bg-green-600 rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
                >
                  Reactivate
                </button>
              )}
              {isActive && !isCancelled && (
                <>
                  <button
                    onClick={() => setTab("plans")}
                    className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 rounded-xl text-sm font-medium transition-colors"
                  >
                    Change Plan
                  </button>
                  <button
                    onClick={() => setShowCancelModal(true)}
                    className="px-5 py-2.5 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-xl text-sm font-medium transition-colors text-red-400"
                  >
                    Cancel Subscription
                  </button>
                </>
              )}
              {subscription.last_invoice_url && (
                <a
                  href={subscription.last_invoice_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-5 py-2.5 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-xl text-sm font-medium transition-colors"
                >
                  View Invoice
                </a>
              )}
            </div>
          </div>
        )}

        {/* Plans Tab */}
        {tab === "plans" && (
          <div className="space-y-4">
            {paymentOptions?.show_picker && (
              <div className="bg-gray-900 rounded-2xl p-5 border border-gray-800">
                <p className="text-sm font-semibold text-white mb-3">Payment method</p>
                <div className="flex flex-wrap gap-2">
                  {paymentOptions.enabled.map((gw) => (
                    <button
                      key={gw}
                      type="button"
                      onClick={() => setSelectedGateway(gw)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                        selectedGateway === gw
                          ? "bg-indigo-600 border-indigo-500 text-white"
                          : "bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-600"
                      }`}
                    >
                      {gw.charAt(0).toUpperCase() + gw.slice(1)}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Choose your preferred gateway before subscribing.
                </p>
              </div>
            )}
            {plans.length === 0 ? (
              <p className="text-gray-400">No plans available.</p>
            ) : (
              <>
              {/* Free tier card — always shown first */}
              {(() => {
                const isFreeCurrent = (subscription?.tier === "free" || isExpiredOrFree) && !isActive;
                const freeFeatures = buildFeatureRows(userRole, "free", tierLimits?.tiers?.["free"]);
                return (
                  <div className={`bg-gray-900 rounded-2xl p-5 border transition-colors ${isFreeCurrent ? "border-indigo-500" : "border-gray-800 hover:border-gray-700"}`}>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <TierBadge tier="free" />
                        {isFreeCurrent && <span className="text-xs text-indigo-400 font-medium">Current plan</span>}
                      </div>
                      <div className="flex gap-2">
                        {isActive && (
                          <button
                            onClick={() => setShowCancelModal(true)}
                            disabled={actionLoading}
                            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 text-red-400"
                          >
                            Downgrade to Free
                          </button>
                        )}
                      </div>
                    </div>
                    <PriceBlock pricing={tierLimits?.pricing?.["free"]} currency={tierLimits?.currency ?? "INR"} />
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                      {freeFeatures.map((f) => (
                        <li key={f.label} className="flex items-center justify-between text-sm">
                          <span className="text-gray-400 flex items-center">
                            <span className="mr-2">{f.icon}</span>
                            {f.label}
                            <FeatureInfoTooltip entry={f.action ? featureCatalog[f.action] : undefined} />
                          </span>
                          <span className={`font-medium ${f.dim ? "text-gray-600 line-through" : f.green ? "text-green-400" : "text-white"}`}>
                            {f.value}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })()}

              {plans.map((plan) => {
                const isCurrent = plan.tier === subscription?.tier;
                const isCurrentActive = isCurrent && isActive;
                const features = buildFeatureRows(userRole, plan.tier, tierLimits?.tiers?.[plan.tier]);
                return (
                  <div
                    key={plan.plan_key}
                    className={`bg-gray-900 rounded-2xl p-5 border transition-colors ${
                      isCurrentActive
                        ? "border-indigo-500"
                        : "border-gray-800 hover:border-gray-700"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <TierBadge tier={plan.tier} />
                        {isCurrentActive && (
                          <span className="text-xs text-indigo-400 font-medium">
                            Current plan
                          </span>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {isExpiredOrFree && (
                          <button
                            onClick={() => handleSubscribe(plan.plan_key)}
                            disabled={actionLoading}
                            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                          >
                            Subscribe
                          </button>
                        )}
                        {isActive && !isCurrent && (() => {
                          const TIER_ORDER = ["free", "starter", "mid", "premium", "pro", "studio", "elite"];
                          const currentIdx = TIER_ORDER.indexOf(subscription?.tier ?? "free");
                          const planIdx = TIER_ORDER.indexOf(plan.tier);
                          return planIdx > currentIdx ? (
                            <button
                              onClick={() => handleUpgrade(plan.plan_key)}
                              disabled={actionLoading}
                              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                            >
                              Upgrade
                            </button>
                          ) : (
                            <button
                              onClick={() => handleDowngrade(plan.plan_key)}
                              disabled={actionLoading}
                              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                            >
                              Downgrade
                            </button>
                          );
                        })()}
                      </div>
                    </div>
                    <PriceBlock pricing={tierLimits?.pricing?.[plan.tier]} currency={tierLimits?.currency ?? "INR"} />
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                      {features.map((f) => (
                        <li key={f.label} className="flex items-center justify-between text-sm">
                          <span className="text-gray-400 flex items-center">
                            <span className="mr-2">{f.icon}</span>
                            {f.label}
                            <FeatureInfoTooltip entry={f.action ? featureCatalog[f.action] : undefined} />
                          </span>
                          <span
                            className={`font-medium ${
                              f.dim ? "text-gray-600 line-through" : f.green ? "text-green-400" : "text-white"
                            }`}
                          >
                            {f.value}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
              </>
            )}
          </div>
        )}

        {/* Billing Tab */}
        {tab === "billing" && (
          <div className="space-y-3">
            {events.length === 0 ? (
              <p className="text-gray-400">No billing events yet.</p>
            ) : (
              events.map((ev) => (
                <div
                  key={ev.event_id}
                  className="bg-gray-900 rounded-xl px-5 py-4 border border-gray-800 flex items-center justify-between"
                >
                  <div>
                    <p className="text-sm text-white font-medium">
                      {ev.event_type.replace(/_/g, " ")}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {ev.event_id}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-400">
                      {formatDate(ev.created_at)}
                    </p>
                    <span
                      className={`text-xs ${ev.processed ? "text-green-400" : "text-yellow-400"}`}
                    >
                      {ev.processed ? "processed" : "pending"}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
          <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800 w-full max-w-md">
            <h2 className="text-lg font-semibold mb-4">Cancel Subscription</h2>
            <p className="text-sm text-gray-400 mb-5">
              You'll keep access until the end of your current billing period.
            </p>
            <label className="block text-sm text-gray-400 mb-2">
              Reason
            </label>
            <select
              value={cancelReason}
              onChange={(e) =>
                setCancelReason(e.target.value as CancellationReason)
              }
              className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
            >
              {CANCELLATION_REASONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
            <label className="block text-sm text-gray-400 mb-2">
              Additional note (optional)
            </label>
            <textarea
              value={cancelNote}
              onChange={(e) => setCancelNote(e.target.value)}
              rows={3}
              maxLength={500}
              className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white text-sm mb-6 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 resize-none"
              placeholder="Any additional feedback..."
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowCancelModal(false)}
                className="flex-1 px-4 py-2.5 bg-gray-800 hover:bg-gray-700 rounded-xl text-sm font-medium transition-colors"
              >
                Keep Subscription
              </button>
              <button
                onClick={handleCancel}
                disabled={actionLoading}
                className="flex-1 px-4 py-2.5 bg-red-700 hover:bg-red-600 rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
              >
                {actionLoading ? "Cancelling..." : "Confirm Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
